import { Head, useForm, router } from "@inertiajs/react"
import { useRef, useState } from "react"
import { track } from "@/lib/analytics"
import type { Account } from "@/types"
import DashboardLayout from "@/components/dashboard/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { OptimizedImage } from "@/components/ui/optimized-image"

function formatUkPhone(value: string): string {
  const digits = value.replace(/[^\d+]/g, "")
  const normalised = digits.startsWith("+44") ? "0" + digits.slice(3) : digits
  const d = normalised.replace(/\D/g, "")
  if (d.startsWith("02")) {
    return d.length <= 3 ? d : d.length <= 7 ? `${d.slice(0, 3)} ${d.slice(3)}` : `${d.slice(0, 3)} ${d.slice(3, 7)} ${d.slice(7, 11)}`
  }
  if (/^01[1-9]1|^011[3-9]/.test(d)) {
    return d.length <= 4 ? d : d.length <= 7 ? `${d.slice(0, 4)} ${d.slice(4)}` : `${d.slice(0, 4)} ${d.slice(4, 7)} ${d.slice(7, 11)}`
  }
  if (d.startsWith("07")) {
    return d.length <= 5 ? d : `${d.slice(0, 5)} ${d.slice(5, 11)}`
  }
  if (d.startsWith("01")) {
    return d.length <= 5 ? d : `${d.slice(0, 5)} ${d.slice(5, 11)}`
  }
  return d.slice(0, 11)
}

function formatUkPostcode(value: string): string {
  const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, "")
  if (clean.length <= 4) return clean
  return `${clean.slice(0, -3)} ${clean.slice(-3)}`
}

export default function SettingsShow({
  account,
  current_role,
}: {
  account: Account
  current_role: string
}) {
  const canEdit = current_role === "owner" || current_role === "admin"
  const { data, setData, patch, processing, errors, transform } = useForm({
    name: account.name || "",
    account_type: (account.account_type || "individual") as "individual" | "agency",
    brand_colour: account.brand_colour || "#C41E2A",
    website_url: account.website_url || "",
    agency_name: account.agency_name || "",
    agency_address: account.agency_address || "",
    agency_phone: account.agency_phone || "",
  })

  transform((d) => ({ account: d }))

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    track("account_settings_saved")
    patch("/dashboard/settings")
  }

  return (
    <DashboardLayout>
      <Head title="Settings | QuiteRight" />

      <div className="mb-8">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
          Account settings
        </h1>
        <p className="mt-1 text-warm-grey">
          Manage your account details and brand settings.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-xl space-y-8">
        {/* General */}
        <div className="rounded-xl border border-stone/40 bg-white p-6">
          <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
            General
          </h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Account name</Label>
              <Input
                id="name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                className="mt-1.5"
                disabled={!canEdit}
                required
              />
              {errors.name && <p className="mt-1 text-xs text-red">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="account_type">Account type</Label>
              <select
                id="account_type"
                value={data.account_type}
                onChange={(e) => setData("account_type", e.target.value as "individual" | "agency")}
                className="mt-1.5 w-full rounded-md border border-stone/40 bg-white px-3 py-2 text-sm disabled:opacity-50"
                disabled={!canEdit}
              >
                <option value="individual">Individual</option>
                <option value="agency">Agency</option>
              </select>
            </div>
          </div>
        </div>

        {/* Agency details */}
        {data.account_type === "agency" && (
          <div className="rounded-xl border border-stone/40 bg-white p-6">
            <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
              Agency details
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="agency_name">Agency name</Label>
                <Input
                  id="agency_name"
                  value={data.agency_name}
                  onChange={(e) => setData("agency_name", e.target.value)}
                  className="mt-1.5"
                  disabled={!canEdit}
                />
                {errors.agency_name && (
                  <p className="mt-1 text-xs text-red">{errors.agency_name}</p>
                )}
              </div>
              <div>
                <Label htmlFor="agency_address">Office address</Label>
                <textarea
                  id="agency_address"
                  value={data.agency_address}
                  onChange={(e) => setData("agency_address", e.target.value)}
                  onBlur={() => {
                    const lines = data.agency_address.split("\n")
                    let lastIdx = -1
                    for (let i = lines.length - 1; i >= 0; i--) { if (lines[i].trim()) { lastIdx = i; break } }
                    if (lastIdx >= 0 && /^[A-Z]{1,2}\d/i.test(lines[lastIdx].trim())) {
                      lines[lastIdx] = formatUkPostcode(lines[lastIdx].trim())
                      setData("agency_address", lines.join("\n"))
                    }
                  }}
                  placeholder={"123 High Street\nEdinburgh\nEH1 1AA"}
                  rows={3}
                  className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  disabled={!canEdit}
                />
                <p className="mt-1 text-xs text-warm-grey">
                  Include postcode on the last line.
                </p>
              </div>
              <div>
                <Label htmlFor="agency_phone">Phone number</Label>
                <Input
                  id="agency_phone"
                  type="tel"
                  value={data.agency_phone}
                  onChange={(e) => setData("agency_phone", formatUkPhone(e.target.value))}
                  placeholder="0131 555 0000"
                  className="mt-1.5"
                  disabled={!canEdit}
                />
                {errors.agency_phone && (
                  <p className="mt-1 text-xs text-red">{errors.agency_phone}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Brand */}
        <div className="rounded-xl border border-stone/40 bg-white p-6">
          <h2 className="mb-4 font-display text-lg font-semibold text-charcoal">
            Brand
          </h2>
          <div className="space-y-4">
            {/* Logo */}
            <LogoUpload
              logoUrl={account.logo_url}
              canEdit={canEdit}
            />

            <div>
              <Label htmlFor="brand_colour">Brand colour</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <input
                  type="color"
                  id="brand_colour"
                  value={data.brand_colour}
                  onChange={(e) => setData("brand_colour", e.target.value)}
                  className="size-10 cursor-pointer rounded-lg border border-stone/40"
                  disabled={!canEdit}
                />
                <Input
                  value={data.brand_colour}
                  onChange={(e) => setData("brand_colour", e.target.value)}
                  className="font-mono text-sm"
                  disabled={!canEdit}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="website_url">Website</Label>
              <Input
                id="website_url"
                type="url"
                value={data.website_url}
                onChange={(e) => setData("website_url", e.target.value)}
                placeholder="https://www.example.co.uk"
                className="mt-1.5"
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>

        {canEdit && (
          <Button
            type="submit"
            disabled={processing}
            className="bg-red text-white hover:bg-red-deep"
          >
            {processing ? "Saving..." : "Save changes"}
          </Button>
        )}
      </form>
    </DashboardLayout>
  )
}

function LogoUpload({
  logoUrl,
  canEdit,
}: {
  logoUrl?: string
  canEdit: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setPreview(URL.createObjectURL(file))
    setUploading(true)

    track("logo_uploaded")
    router.post(
      "/dashboard/settings/upload_logo",
      { logo: file },
      {
        preserveScroll: true,
        forceFormData: true,
        onFinish: () => {
          setUploading(false)
          setPreview(null)
        },
      },
    )
  }

  function handleRemove() {
    track("logo_removed")
    router.delete("/dashboard/settings/delete_logo", {
      preserveScroll: true,
    })
  }

  const displayUrl = preview || logoUrl

  return (
    <div>
      <Label>Agency logo</Label>
      <div className="mt-2 flex items-start gap-4">
        {/* Preview */}
        <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-stone/40 bg-stone/10">
          {displayUrl ? (
            <OptimizedImage
              src={displayUrl}
              alt="Agency logo"
              className="size-full object-contain p-1"
            />
          ) : (
            <svg className="size-8 text-warm-grey/30" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
            </svg>
          )}
        </div>

        {/* Controls */}
        <div className="flex-1">
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? "Uploading..." : logoUrl ? "Change logo" : "Upload logo"}
              </Button>
              {logoUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemove}
                  className="text-warm-grey hover:text-red"
                >
                  Remove
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          )}
          <p className="mt-2 text-[11px] leading-relaxed text-warm-grey">
            Square image, at least 200 &times; 200px. PNG with transparent background
            works best. JPEG and WebP also accepted. Max 2 MB.
          </p>
        </div>
      </div>
    </div>
  )
}
